import { useState, useEffect, useContext, createContext } from 'react';
import Validator from 'validozer';
import { FormEvent } from './FormEvent';

export const FormContext = createContext(null);

export const useFormSupplier = () => {
	return useContext(FormContext);
};

const formUpdater = (schema, target, alias) => {
	let { name, value, form, files, selectedOptions, type } = target;

	if (['checkbox', 'radio'].includes(type)) {
		value = Array.from(form.querySelectorAll(`[name="${name}"]`))
			.filter((element) => element.checked)
			.map((box) => box.value);
		if (type === 'radio') {
			value = value[0];
		}
	}

	if (alias) {
		name = alias;
	}

	if (files) {
		value = files;
	}

	if (selectedOptions && target.multiple) {
		value = Array.from(selectedOptions);
	}

	if (!schema[name]) return schema;

	const updateState = {
		...schema[name],
		isValidated: true,
		value,
	};

	const { label, rules } = updateState;

	if (rules) {
		Object.assign(
			updateState,
			Validator.validate(
				value, 
				rules, 
				label || target.name, 
				Object.keys(schema).reduce((result, key) => { 
					result[key] = schema[key].value
					return result
				}, {})
			)
		);
	}

	return { ...schema, [name]: updateState };
};

const findOrCreate = (state = {}) => ({
	value: '',
	isInvalid: false,
	isValidated: false,
	message: null,
	aliasOf: null,
	...state,
});

const immutableSchema = (originalSchema) => (
	Object.keys(originalSchema).reduce((result, name) => {
		result[name] = {...originalSchema[name]}
		return result;
	}, {})
)

const resetSchema = (originalSchema) => (
	Object.keys(originalSchema).reduce((result, name) => {
		result[name] = {
			...originalSchema[name],
			isInvalid: false,
			isValidated: false,
			message: null,
			value: '',
		} 
		return result;
	}, {})
)

export const useForm = (defaultSchema = {}) => {
	const [schema, setSchema] = useState(
		Object.keys(defaultSchema).reduce((result, name) => {
			result[name] = {
				value: '',
				...defaultSchema[name],
				isInvalid: false,
				isValidated: false,
				message: null,
				aliasOf: null,
			};
			return result;
		}, {})
	);

	const formState = (get) =>
		typeof get === 'string'
			? findOrCreate(schema[get])
			: immutableSchema(schema) 

	const formUpdate = ({ target }, alias) => {
		setSchema((state) => formUpdater(state, target, alias));
	};

	const useFormWhenMount = ({
		name,
		label,
		rules,
		value = '',
		alias = null,
	}) => {
		useEffect(() => {
			// if (!rules) return;

			setSchema((state) => ({
				...state,
				[alias || name]: {
					label,
					rules,
					isInvalid: false,
					isValidated: false,
					message: null,
					value,
					aliasOf: alias && name,
				},
			}));

			return () => {
				setSchema((state) => {
					delete state[alias || name];
					return { ...state };
				});
			};
		}, [name, label, rules, value, alias]);
	};

	const handlerSubmit = (onSubmit) => {
		return (event) => {
			const isSubmit = event.type === 'submit'
			isSubmit && event.preventDefault();
			const target = event.target;  
			const updatedSchema = Object.keys(schema).reduce((result, name) => {
				return target
					? formUpdater(result, { name, value: result[name].value })
					: result;
			}, immutableSchema(schema));

			setSchema(updatedSchema);

			const reset = () => {
				setSchema(resetSchema(updatedSchema));
				isSubmit && target.reset();
			};

			const formevent = new FormEvent(target, immutableSchema(updatedSchema), reset);
			formevent.type = event.type
			formevent.originalEvent = event
			typeof onSubmit === 'function' && onSubmit(formevent);
		};
	};

	return {
		formState,
		formUpdate,
		formSubmit: handlerSubmit,
		formRegistry: useFormWhenMount,
	};
};
